import React, { useState, useEffect } from "react";
import { Calendar, MapPin, ShoppingCart } from "lucide-react";
import arrow from "../../../../../../assets/images/arrow.png";
import defaultAvatar from "../../../../../../assets/images/default_avatar.jpg";

const LabOrderDetails: React.FC<{
  orderStatus: "new" | "awaiting" | "approved";
}> = ({ orderStatus }) => {
  const [step, setStep] = useState<"notify" | "awaiting" | "approved">(
    "notify"
  );

  useEffect(() => {
    if (orderStatus === "new") setStep("notify");
    else if (orderStatus === "awaiting") setStep("awaiting");
    else if (orderStatus === "approved") setStep("approved");
  }, [orderStatus]);

  return (
    <div className="w-full bg-white p-6 font-sans">
      {/* Header */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-lg font-medium text-gray-600 mb-1">
            Order ID: DNDC14M147
          </h1>
        </div>
        <div className="text-right">
          <span className="text-gray-600 mr-2">Payment :</span>
          <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
            Paid
          </span>
        </div>
      </div>

      {/* Order Details */}
      <div className="mb-8">
        <h2 className="text-gray-600 text-sm mb-4">Order Details</h2>
        <div className="flex items-center justify-between bg-cyan-50 p-4 rounded-lg">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4">
              <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-md flex items-center justify-center">
                <div className="w-3 h-3 bg-white rounded-full"></div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">
                Basic Hair Fall Check
              </h3>
              <p className="text-gray-500 text-sm">Lab Test</p>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center mb-1">
              <span className="text-2xl font-bold text-green-600">₹ 350</span>
              <span className="text-gray-400 line-through ml-2">₹699</span>
            </div>
            <p className="text-gray-600 text-sm">2 Tests</p>
          </div>
        </div>
      </div>

      {/* Patients Include */}
      <div className="mb-8">
        <h2 className="text-gray-600 text-sm mb-4">Patients Include</h2>
        <div className="flex items-center">
          <div className="w-10 h-10 rounded-full overflow-hidden mr-3">
            <img
              src={defaultAvatar}
              alt="not-found"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <p className="font-medium text-gray-900">Vikash Verma</p>
            <p className="text-gray-500 text-sm">Male · 60yrs</p>
          </div>
        </div>
      </div>

      {/* Appointment */}
      <div className="mb-8">
        <h2 className="text-gray-600 text-sm mb-4">Appointment</h2>
        <div className="flex items-center">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
            <Calendar className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-gray-900 font-medium">
            Wednesday, 10 Jan 2024, 11:30
          </p>
        </div>
      </div>

      {/* Patient Address */}
      <div className="mb-8">
        <div className="flex items-start mb-2">
          <MapPin className="w-4 h-4 text-red-500 mr-2 mt-0.5" />
          <h2 className="text-gray-600 text-sm">Patient Address</h2>
        </div>
        <div className="ml-6">
          <p className="text-gray-900">
            Banjara Hills Rd Number 1, Balapur Basthi, Banjara Hills, Hyderabad
          </p>
          <p className="text-gray-600 text-sm mt-1">Pin Code: 500034</p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-between items-end pt-6 border-t border-gray-200">
        {/* Ordered By */}
        <div className="mb-4 xl:mb-0 w-full xl:w-auto">
          <div className="mt-3 mb-4">
            <p className="text-gray-700 mb-2">Ordered by</p>
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                <img
                  src={defaultAvatar}
                  alt="user"
                  className="w-8 h-8 rounded-full"
                />
              </div>
              <div>
                <p className="font-medium">John Doe</p>
                <p className="text-gray-500 text-xs">+91 98765 43210</p>
              </div>
            </div>
          </div>
        </div>

        {/* Arrow */}
        <div className="hidden xl:block">
          <img src={arrow} alt="arrow" className="w-11 h-4" />
        </div>

        {/* Assign to Clinic */}
        <div className="w-full xl:w-auto">
          <div className="mt-3 mb-4">
            <div className="flex items-center">
              <div className="w-full">
                <p>Nearest Clinic</p>
                <div className="bg-gray-100 p-2 rounded text-gray-700">
                  Clinic Name Placeholder
                </div>
              </div>
            </div>
            <p className="text-gray-500 text-xs mt-2">
              Date will be generated once order is processed
            </p>
          </div>
        </div>
      </div>

      {/* Order status display */}
      <div className="mb-4">
        <p className="text-gray-700">
          Status:
          <span className="ml-2 font-medium text-blue-500">
            Forwarded To Clinic
          </span>
        </p>
        <p className="text-gray-500 text-xs">Order Date: 02 Jun 2025</p>
      </div>

      {/* When new orders from dropdown is selected */}
      {step === "notify" && (
        <button
          className="bg-green-600  text-white py-2 px-6 rounded-lg transition-colors"
           onClick={() => setStep("awaiting")}
        >
          Notify Patient
        </button>
      )}

      {/* When awaiting approval from dropdown is selected */}
      {step === "awaiting" && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 w-full max-w-md">
          <div className="flex items-center justify-between border-b pb-2 mb-2">
            <div className="flex items-center gap-2 text-gray-700">
              <ShoppingCart className="w-4 h-4" />
              <span className="text-sm font-medium">2 items</span>
            </div>
          </div>

          {/* Bottom Row - Total & Button */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-500">Total payable</div>
              <div className="text-xl font-semibold text-gray-900">₹350</div>
            </div>
            <p className="text-sm px-4 py-2 text-orange-600">
              Patient Approval Pending
            </p>
          </div>
        </div>
      )}

      {/* when approved is selected from dropdown */}
      { step === "approved" && (
        <button
          className="bg-green-600  text-white py-2 px-6 rounded-lg transition-colors"
          onClick={() => alert("Order Processed")}
        >
          Process Order
        </button>
      )}
    </div>
  );
};

export default LabOrderDetails;
